import { useState } from "react";
import { Tag, Folder, Plus, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { TranslationEntry } from "@/hooks/useTranslationHistory";
import { useToast } from "@/hooks/use-toast";

interface EntryManagementProps {
  entry: TranslationEntry;
  availableCategories: string[];
  availableTags: string[];
  onUpdateEntry: (id: string, updates: Partial<TranslationEntry>) => void;
}

export function EntryManagement({ 
  entry, 
  availableCategories, 
  availableTags,
  onUpdateEntry 
}: EntryManagementProps) {
  const { toast } = useToast();
  const [tagsOpen, setTagsOpen] = useState(false);
  const [categoryOpen, setCategoryOpen] = useState(false);
  const [newTag, setNewTag] = useState("");
  const [newCategory, setNewCategory] = useState("");

  const handleAddTag = () => {
    if (!newTag.trim()) return;
    
    const currentTags = entry.tags || [];
    if (currentTags.includes(newTag.trim())) {
      toast({
        title: "Tag already exists",
        description: "This tag is already added to this translation",
        variant: "destructive",
      });
      return;
    }

    const updatedTags = [...currentTags, newTag.trim()];
    onUpdateEntry(entry.id, { tags: updatedTags });
    setNewTag("");
    toast({
      title: "Tag added",
      description: `Added "${newTag.trim()}" to translation`,
    });
  };

  const handleRemoveTag = (tagToRemove: string) => {
    const updatedTags = (entry.tags || []).filter(tag => tag !== tagToRemove);
    onUpdateEntry(entry.id, { tags: updatedTags });
    toast({
      title: "Tag removed",
      description: `Removed "${tagToRemove}" from translation`,
    });
  };

  const handleSetCategory = (category: string) => {
    const categoryToSet = category === "none" ? undefined : category;
    onUpdateEntry(entry.id, { category: categoryToSet });
    setCategoryOpen(false);
    toast({
      title: "Category updated",
      description: categoryToSet ? `Set category to "${categoryToSet}"` : "Removed category",
    });
  };

  const handleAddNewCategory = () => {
    if (!newCategory.trim()) return;
    
    handleSetCategory(newCategory.trim());
    setNewCategory("");
  };

  return (
    <div className="flex items-center gap-2 text-xs">
      {/* Category Management */}
      <Popover open={categoryOpen} onOpenChange={setCategoryOpen}>
        <PopoverTrigger asChild>
          <Button variant="ghost" size="sm" className="h-6 px-2 text-xs">
            <Folder className="h-3 w-3 mr-1" />
            {entry.category || "No category"}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-56 p-3 z-50">
          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium">Select Category</label>
              <Select value={entry.category || "none"} onValueChange={handleSetCategory}>
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue placeholder="Choose category" />
                </SelectTrigger>
                <SelectContent className="z-50 bg-popover">
                  <SelectItem value="none">No category</SelectItem>
                  {availableCategories.map((category) => (
                    <SelectItem key={category} value={category}>
                      {category}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <label className="text-xs font-medium">New Category</label>
              <div className="flex gap-1 mt-1">
                <Input
                  placeholder="Category name"
                  value={newCategory}
                  onChange={(e) => setNewCategory(e.target.value)}
                  className="h-7 text-xs"
                  onKeyPress={(e) => e.key === 'Enter' && handleAddNewCategory()}
                />
                <Button onClick={handleAddNewCategory} size="sm" className="h-7 px-2">
                  <Plus className="h-3 w-3" />
                </Button>
              </div>
            </div>
          </div>
        </PopoverContent>
      </Popover>

      {/* Tags Display & Management */}
      <div className="flex items-center gap-1">
        {entry.tags?.map((tag) => (
          <Badge
            key={tag}
            variant="secondary"
            className="text-xs h-5 px-1 cursor-pointer group hover:bg-destructive hover:text-destructive-foreground"
            onClick={() => handleRemoveTag(tag)}
          >
            #{tag}
            <X className="h-2 w-2 ml-1 opacity-0 group-hover:opacity-100" />
          </Badge>
        ))}

        {/* Add Tag */}
        <Popover open={tagsOpen} onOpenChange={setTagsOpen}>
          <PopoverTrigger asChild>
            <Button variant="ghost" size="sm" className="h-5 w-5 p-0 text-muted-foreground hover:text-foreground">
              <Plus className="h-3 w-3" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-48 p-3 z-50">
            <div className="space-y-3">
              <div>
                <label className="text-xs font-medium">Add Tag</label>
                <div className="flex gap-1 mt-1">
                  <Input
                    placeholder="Tag name"
                    value={newTag}
                    onChange={(e) => setNewTag(e.target.value)}
                    className="h-7 text-xs"
                    onKeyPress={(e) => e.key === 'Enter' && handleAddTag()}
                  />
                  <Button onClick={handleAddTag} size="sm" className="h-7 px-2">
                    <Plus className="h-3 w-3" />
                  </Button>
                </div>
              </div>
              
              {availableTags.length > 0 && (
                <div>
                  <label className="text-xs font-medium">Quick Tags</label>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {availableTags
                      .filter(tag => !entry.tags?.includes(tag))
                      .slice(0, 6)
                      .map((tag) => (
                        <Badge
                          key={tag}
                          variant="outline"
                          className="text-xs h-5 px-1 cursor-pointer hover:bg-primary hover:text-primary-foreground"
                          onClick={() => {
                            const updatedTags = [...(entry.tags || []), tag];
                            onUpdateEntry(entry.id, { tags: updatedTags });
                            toast({
                              title: "Tag added",
                              description: `Added "${tag}" to translation`,
                            });
                          }}
                        >
                          #{tag}
                        </Badge>
                      ))}
                  </div>
                </div>
              )}
            </div>
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
}